/**
 * Twilio Field Mapping Utility
 * Maps Twilio API field names to user-friendly names and provides actionable guidance
 */

export const TWILIO_FIELD_MAPPING = {
  // Business Information
  business_name: {
    label: 'Business Name',
    formField: 'organization_name',
    guidance: 'Enter the official legal name of your organization as it appears on business registration documents.'
  },
  business_website: {
    label: 'Business Website',
    formField: 'business_website',
    guidance: 'Enter a valid website URL starting with http:// or https://. The website should be active and accessible.'
  },
  business_street_address: {
    label: 'Street Address',
    formField: 'business_address',
    guidance: 'Enter the full street address including building number and street name.'
  },
  business_street_address_2: {
    label: 'Address Line 2',
    formField: 'business_address_line_2',
    guidance: 'Enter suite, unit, or apartment number if applicable.'
  },
  business_city: {
    label: 'City',
    formField: 'business_city',
    guidance: 'Enter the city name where your business is located.'
  },
  business_state_province_region: {
    label: 'State',
    formField: 'business_state',
    guidance: 'Enter the two-letter state abbreviation (e.g., CA, NY, TX).'
  },
  business_postal_code: {
    label: 'Postal Code',
    formField: 'business_postal_code',
    guidance: 'Enter a valid US ZIP code (e.g., 12345 or 12345-6789).'
  },
  business_country: {
    label: 'Country',
    formField: 'business_country',
    guidance: 'Enter the two-letter country code (e.g., US for United States).'
  },

  // Business Contact
  business_contact_first_name: {
    label: 'Contact First Name',
    formField: 'business_contact_first_name',
    guidance: 'Enter the first name of the primary business contact person.'
  },
  business_contact_last_name: {
    label: 'Contact Last Name',
    formField: 'business_contact_last_name',
    guidance: 'Enter the last name of the primary business contact person.'
  },
  business_contact_email: {
    label: 'Contact Email',
    formField: 'business_contact_email',
    guidance: 'Enter a valid business email address (e.g., contact@company.com).'
  },
  business_contact_phone: {
    label: 'Contact Phone',
    formField: 'business_contact_phone',
    guidance: 'Enter a valid phone number in E.164 format (e.g., +12125551234).'
  },

  // Registration Information
  business_registration_number: {
    label: 'EIN/Tax ID',
    formField: 'business_registration_number',
    guidance: 'Enter your 9-digit Employer Identification Number (EIN) without dashes (e.g., 123456789).'
  },
  business_registration_identifier: {
    label: 'Registration Type',
    formField: 'business_registration_identifier',
    guidance: 'Select the type of business registration (EIN, 501c3, etc.).'
  },
  business_identity: {
    label: 'Business Type',
    formField: 'business_identity',
    guidance: 'Select the type of organization (nonprofit_501c3, nonprofit_church, etc.).'
  },

  // Phone Number and Messaging
  phone_number: {
    label: 'Phone Number',
    formField: 'phone_number',
    guidance: 'Enter the toll-free number in E.164 format (e.g., +18005551234). The number must be purchased in your Twilio account.'
  },
  messaging_service_sid: {
    label: 'Messaging Service',
    formField: 'messaging_service_sid',
    guidance: 'Enter a valid Twilio Messaging Service SID (starts with MG). This service must exist in your account.'
  },
  message_volume: {
    label: 'Message Volume',
    formField: 'message_volume',
    source: 'config',
    guidance: 'Select the expected monthly message volume. Use string values: "10" for 1,000 messages, "100" for 10,000, etc.'
  },

  // Use Case
  use_case_summary: {
    label: 'Use Case Summary',
    formField: 'use_case_summary',
    source: 'config',
    guidance: 'Provide a clear, detailed description of how you will use this toll-free number for messaging (minimum 100 characters).'
  },
  use_case_categories: {
    label: 'Use Case Categories',
    formField: 'use_case_categories',
    source: 'config',
    guidance: 'Select all applicable categories that describe your messaging use case.'
  },
  production_message_sample: {
    label: 'Sample Message',
    formField: 'production_message_sample',
    source: 'config',
    guidance: 'The sample message is shared by every submission of this type and is set in Submission Configs, not on the submission. Twilio allows at most 1,000 characters, including the opt-out disclosure.'
  },

  // Opt-in
  opt_in_image_urls: {
    label: 'Opt-in Image',
    formField: 'opt_in_image_urls',
    guidance: 'Upload a screenshot showing how users opt-in to receive messages. The image must be publicly accessible.'
  },
  opt_in_type: {
    label: 'Opt-in Type',
    formField: 'opt_in_type',
    source: 'config',
    guidance: 'Select how users provide consent to receive messages (VERBAL, WEB_FORM, PAPER_FORM, etc.).'
  },

  // Customer Profile
  customer_profile_sid: {
    label: 'Customer Profile',
    formField: 'customer_profile_sid',
    guidance: 'The Trust Hub Customer Profile SID must be approved (status: twilio-approved) before submission.'
  }
};

/**
 * Get user-friendly field information from Twilio field name
 * @param {string} twilioFieldName - The field name from Twilio error (e.g., "business_website")
 * @returns {object|null} Field information or null if not found
 */
export function getFieldInfo(twilioFieldName) {
  return TWILIO_FIELD_MAPPING[twilioFieldName] || null;
}

/**
 * Extract actionable error message from Twilio error response
 * @param {object} errorResponse - The error response from backend
 * @returns {object} Structured error information
 */
export function parseErrorResponse(errorResponse) {
  const result = {
    title: 'Submission Failed',
    message: errorResponse.message || 'Failed to submit to Twilio',
    originalMessage: errorResponse.originalMessage,
    code: errorResponse.code,
    fieldError: null,
    fieldInfo: null,
    actionableGuidance: null
  };

  // If we have field-specific error information
  if (errorResponse.fieldError) {
    const { field, rawError } = errorResponse.fieldError;
    const fieldInfo = getFieldInfo(field);

    result.fieldError = field;
    result.fieldInfo = fieldInfo;

    // Build actionable guidance
    if (fieldInfo) {
      result.title = `Invalid ${fieldInfo.label}`;
      result.actionableGuidance = fieldInfo.guidance;

      // "Invalid parameter <field>: <reason>" carries a specific reason (e.g.
      // the backend's sample-length check); show it rather than a generic line.
      const reason = rawError.match(new RegExp(`${field}\\s*:\\s*(.+)$`, 'is'))?.[1]?.trim();

      // Customize message based on error pattern
      if (reason) {
        result.message = reason.charAt(0).toUpperCase() + reason.slice(1);
      } else if (rawError.toLowerCase().includes('cannot parse')) {
        result.message = `The ${fieldInfo.label} value is not in the correct format.`;
      } else if (rawError.toLowerCase().includes('required') || rawError.toLowerCase().includes('missing')) {
        result.message = `The ${fieldInfo.label} field is required and cannot be empty.`;
      } else if (rawError.toLowerCase().includes('invalid')) {
        result.message = `The ${fieldInfo.label} value is invalid.`;
      } else {
        result.message = rawError;
      }
    } else {
      // No field mapping found, use raw error
      result.title = `Validation Error (${field})`;
      result.message = rawError;
      result.actionableGuidance = `Please check the "${field}" field and ensure it contains valid data.`;
    }
  }

  return result;
}

/**
 * Get all validation rules for display in help text
 * @returns {array} Array of field validation rules
 */
export function getAllValidationRules() {
  return Object.entries(TWILIO_FIELD_MAPPING).map(([field, info]) => ({
    field,
    ...info
  }));
}

/**
 * Format error for user display
 * @param {object} parsedError - Parsed error from parseErrorResponse
 * @returns {string} Formatted error message for display
 */
export function formatErrorForDisplay(parsedError) {
  let message = `${parsedError.title}: ${parsedError.message}`;

  if (parsedError.actionableGuidance) {
    message += `\n\nℹ️ ${parsedError.actionableGuidance}`;
  }

  if (parsedError.originalMessage && parsedError.originalMessage !== parsedError.message) {
    message += `\n\nTechnical Details: ${parsedError.originalMessage}`;
  }

  if (parsedError.code) {
    message += `\n\nError Code: ${parsedError.code}`;
  }

  return message;
}
